import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, Plus, Trash2, Loader2, AlertCircle, Building2 } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';

const PHASE_OPTIONS = [
  { value: 'pre_launch',   label: 'Pré-lançamento' },
  { value: 'launch',       label: 'Lançamento' },
  { value: 'construction', label: 'Em construção' },
  { value: 'delivered',    label: 'Entregue' },
];

const STATUS_OPTIONS = [
  { value: 'active',    label: 'Ativo' },
  { value: 'inactive',  label: 'Inativo' },
  { value: 'suspended', label: 'Suspenso' },
  { value: 'cancelled', label: 'Cancelado' },
];

const TIPO_OPTIONS = [
  'studio', '1q', '2q', '3q', '4q',
  'cobertura', 'garden', 'duplex', 'penthouse',
  'loja', 'sala', 'vaga', 'other',
];

const ROLE_OPTIONS = [
  { value: 'developer',    label: 'Developer' },
  { value: 'co_developer', label: 'Co-developer' },
  { value: 'investor',     label: 'Investor' },
  { value: 'partner',      label: 'Partner' },
];

const STATES_BR = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

function slugify(s) {
  return (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function parseNum(v) {
  if (v === '' || v == null) return null;
  const cleaned = String(v).replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.');
  const n = Number(cleaned);
  return isFinite(n) ? n : null;
}

const EMPTY_TIPOLOGIA = { tipo: '2q', label: '', area_privativa_min: '', area_privativa_max: '', suites: '', vagas: '', total_units: '' };
const EMPTY_INCORP    = { empresa_id: '', role: 'developer', participation_pct: '', is_lead: false };

export default function EmpreendimentoCreateModal({ isOpen, onClose, onCreated }) {
  const { user, loading: authLoading } = useAuth();

  const [empresas, setEmpresas] = useState([]);
  const [empresaSearch, setEmpresaSearch] = useState('');

  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [phase, setPhase] = useState('launch');
  const [status, setStatus] = useState('active');
  const [city, setCity] = useState('');
  const [state, setState] = useState('SP');
  const [neighborhood, setNeighborhood] = useState('');
  const [addressStreet, setAddressStreet] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [launchDate, setLaunchDate] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [description, setDescription] = useState('');

  const [tipologias, setTipologias] = useState([{ ...EMPTY_TIPOLOGIA }]);
  const [incorporadoras, setIncorporadoras] = useState([{ ...EMPTY_INCORP }]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('empresas')
        .select('id, empresa, company_name, papel_negocio')
        .order('empresa');
      if (!cancelled) setEmpresas(data || []);
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name));
  }, [name, slugTouched]);

  const filteredEmpresas = useMemo(() => {
    if (!empresaSearch.trim()) return empresas.slice(0, 50);
    const q = empresaSearch.trim().toLowerCase();
    return empresas
      .filter((e) => (e.empresa || e.company_name || '').toLowerCase().includes(q))
      .slice(0, 50);
  }, [empresaSearch, empresas]);

  const formValid = name.trim() && city.trim() && state.trim() && phase && status;

  const addTipologia = () => setTipologias((arr) => [...arr, { ...EMPTY_TIPOLOGIA }]);
  const removeTipologia = (i) => setTipologias((arr) => arr.filter((_, idx) => idx !== i));
  const updateTipologia = (i, key, value) => setTipologias((arr) => arr.map((row, idx) => idx === i ? { ...row, [key]: value } : row));

  const addIncorp = () => setIncorporadoras((arr) => [...arr, { ...EMPTY_INCORP }]);
  const removeIncorp = (i) => setIncorporadoras((arr) => arr.filter((_, idx) => idx !== i));
  const updateIncorp = (i, key, value) => setIncorporadoras((arr) => arr.map((row, idx) => idx === i ? { ...row, [key]: value } : row));

  const handleSubmit = useCallback(async () => {
    if (authLoading || !user?.id) {
      setError('Sessão não confirmada. Recarregue a página.');
      return;
    }
    if (!formValid) {
      setError('Preencha nome, cidade, estado, fase e status.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        name: name.trim(),
        short_name: shortName.trim() || null,
        slug: slug.trim() || slugify(name),
        phase,
        status,
        address_street: addressStreet.trim() || null,
        address_number: addressNumber.trim() || null,
        neighborhood: neighborhood.trim() || null,
        city: city.trim(),
        state: state.trim().toUpperCase(),
        zip_code: zipCode.trim() || null,
        launch_date: launchDate || null,
        expected_delivery: expectedDelivery || null,
        description: description.trim() || null,
        source_type: 'manual',
        created_by: user.id,
        updated_by: user.id,
      };

      const { data: empRow, error: empErr } = await supabase
        .from('empreendimentos')
        .insert(payload)
        .select('id')
        .single();
      if (empErr) throw empErr;
      const empreendimentoId = empRow.id;

      const validTipologias = tipologias
        .filter((t) => t.tipo)
        .map((t) => ({
          empreendimento_id: empreendimentoId,
          tipo: t.tipo,
          label: t.label.trim() || null,
          area_privativa_min: parseNum(t.area_privativa_min),
          area_privativa_max: parseNum(t.area_privativa_max),
          total_units: parseNum(t.total_units),
          suites: parseNum(t.suites),
          vagas: parseNum(t.vagas),
          created_by: user.id,
          updated_by: user.id,
        }));
      if (validTipologias.length > 0) {
        const { error: tipErr } = await supabase.from('empreendimento_tipologias').insert(validTipologias);
        if (tipErr) throw tipErr;
      }

      const validIncorps = incorporadoras
        .filter((i) => i.empresa_id)
        .map((i) => ({
          empreendimento_id: empreendimentoId,
          empresa_id: i.empresa_id,
          role: i.role || 'developer',
          participation_pct: parseNum(i.participation_pct),
          is_lead: !!i.is_lead,
          created_by: user.id,
          updated_by: user.id,
        }));
      if (validIncorps.length > 0) {
        const { error: incErr } = await supabase.from('empreendimento_incorporadoras').insert(validIncorps);
        if (incErr) throw incErr;
      }

      onCreated?.({ id: empreendimentoId, name: payload.name });
      onClose();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }, [authLoading, user, formValid, name, shortName, slug, phase, status, addressStreet, addressNumber, neighborhood, city, state, zipCode, launchDate, expectedDelivery, description, tipologias, incorporadoras, onCreated, onClose]);

  if (!isOpen) return null;

  const inputCls = 'w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-violet-500';

  return (
    <div className="fixed inset-0 bg-black/50 z-[10005] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 size={18} className="text-violet-600" />
            <h2 className="text-base font-bold text-slate-800">Novo Empreendimento</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" title="Fechar">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800 flex items-start gap-1.5">
              <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}

          <section>
            <h3 className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2">Identidade</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Nome *</label>
                <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: TEÇÁ" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Short name</label>
                <input className={inputCls} value={shortName} onChange={(e) => setShortName(e.target.value)} placeholder="Opcional" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Slug</label>
                <input className={inputCls} value={slug} onChange={(e) => { setSlug(e.target.value); setSlugTouched(true); }} placeholder="auto" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Fase *</label>
                <select className={`${inputCls} bg-white`} value={phase} onChange={(e) => setPhase(e.target.value)}>
                  {PHASE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Status *</label>
                <select className={`${inputCls} bg-white`} value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2">Endereço</h3>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="md:col-span-4">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Rua</label>
                <input className={inputCls} value={addressStreet} onChange={(e) => setAddressStreet(e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Número</label>
                <input className={inputCls} value={addressNumber} onChange={(e) => setAddressNumber(e.target.value)} />
              </div>
              <div className="md:col-span-3">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Bairro</label>
                <input className={inputCls} value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Cidade *</label>
                <input className={inputCls} value={city} onChange={(e) => setCity(e.target.value)} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">UF *</label>
                <select className={`${inputCls} bg-white`} value={state} onChange={(e) => setState(e.target.value)}>
                  {STATES_BR.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">CEP</label>
                <input className={inputCls} value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="00000-000" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Lançamento</label>
                <input type="date" className={inputCls} value={launchDate} onChange={(e) => setLaunchDate(e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Entrega prevista</label>
                <input type="date" className={inputCls} value={expectedDelivery} onChange={(e) => setExpectedDelivery(e.target.value)} />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2">Descrição</h3>
            <textarea
              rows={2}
              className={`${inputCls} resize-none`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Diferenciais, breve descrição..."
            />
          </section>

          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Tipologias</h3>
              <button
                type="button"
                onClick={addTipologia}
                className="text-[11px] text-violet-700 hover:underline flex items-center gap-1"
              >
                <Plus size={11} /> Adicionar
              </button>
            </div>
            <div className="space-y-2">
              {tipologias.map((t, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 items-end bg-slate-50 border border-slate-200 rounded p-2">
                  <div className="col-span-2">
                    <label className="block text-[10px] text-slate-500 mb-0.5">Tipo</label>
                    <select className={`${inputCls} bg-white`} value={t.tipo} onChange={(e) => updateTipologia(i, 'tipo', e.target.value)}>
                      {TIPO_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <label className="block text-[10px] text-slate-500 mb-0.5">Label</label>
                    <input className={inputCls} value={t.label} onChange={(e) => updateTipologia(i, 'label', e.target.value)} placeholder="Ex: Nature 4Q" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[10px] text-slate-500 mb-0.5">Área min (m²)</label>
                    <input className={inputCls} value={t.area_privativa_min} onChange={(e) => updateTipologia(i, 'area_privativa_min', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[10px] text-slate-500 mb-0.5">Área max (m²)</label>
                    <input className={inputCls} value={t.area_privativa_max} onChange={(e) => updateTipologia(i, 'area_privativa_max', e.target.value)} />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-[10px] text-slate-500 mb-0.5">Suítes</label>
                    <input className={inputCls} value={t.suites} onChange={(e) => updateTipologia(i, 'suites', e.target.value)} />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-[10px] text-slate-500 mb-0.5">Vagas</label>
                    <input className={inputCls} value={t.vagas} onChange={(e) => updateTipologia(i, 'vagas', e.target.value)} />
                  </div>
                  <div className="col-span-1 flex items-center justify-center">
                    {tipologias.length > 1 && (
                      <button type="button" onClick={() => removeTipologia(i)} className="text-slate-400 hover:text-red-600" title="Remover">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Incorporadoras</h3>
              <button
                type="button"
                onClick={addIncorp}
                className="text-[11px] text-violet-700 hover:underline flex items-center gap-1"
              >
                <Plus size={11} /> Adicionar
              </button>
            </div>
            <div className="mb-2">
              <input
                type="text"
                placeholder="Filtrar empresa (afeta todos os selects abaixo)"
                value={empresaSearch}
                onChange={(e) => setEmpresaSearch(e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              {incorporadoras.map((row, i) => (
                <div key={i} className="grid grid-cols-12 gap-1.5 items-end bg-slate-50 border border-slate-200 rounded p-2">
                  <div className="col-span-5">
                    <label className="block text-[10px] text-slate-500 mb-0.5">Empresa</label>
                    <select className={`${inputCls} bg-white`} value={row.empresa_id} onChange={(e) => updateIncorp(i, 'empresa_id', e.target.value)}>
                      <option value="">— escolha —</option>
                      {filteredEmpresas.map((e) => (
                        <option key={e.id} value={e.id}>{e.empresa || e.company_name || '(sem nome)'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <label className="block text-[10px] text-slate-500 mb-0.5">Role</label>
                    <select className={`${inputCls} bg-white`} value={row.role} onChange={(e) => updateIncorp(i, 'role', e.target.value)}>
                      {ROLE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[10px] text-slate-500 mb-0.5">% Part.</label>
                    <input className={inputCls} value={row.participation_pct} onChange={(e) => updateIncorp(i, 'participation_pct', e.target.value)} placeholder="0-100" />
                  </div>
                  <div className="col-span-1 flex items-center justify-center pb-1.5">
                    <input type="checkbox" checked={row.is_lead} onChange={(e) => updateIncorp(i, 'is_lead', e.target.checked)} title="Lead" />
                  </div>
                  <div className="col-span-1 flex items-center justify-center">
                    {incorporadoras.length > 1 && (
                      <button type="button" onClick={() => removeIncorp(i)} className="text-slate-400 hover:text-red-600" title="Remover">
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-slate-400 mt-1">Lead = incorporadora principal do empreendimento.</div>
          </section>
        </div>

        <div className="p-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded">
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={authLoading || !user?.id || submitting || !formValid}
            className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 hover:bg-violet-700"
          >
            {submitting && <Loader2 size={12} className="animate-spin" />}
            {authLoading ? 'Carregando sessão...' : (submitting ? 'Salvando...' : 'Criar empreendimento')}
          </button>
        </div>
      </div>
    </div>
  );
}
